import React, { useEffect } from 'react';
import { push } from 'connected-react-router/immutable';
import { useDispatch } from 'react-redux';
import { useAppSelector } from 'app/helpers/hooks';
import { selectCurrentIntake, selectFurthestProductStepPath } from 'app/selectors/customer';
import { apiRequestUserCommand } from 'app/actions/customer';
import { Button } from 'mui';
import './css/TestosteroneIncreaseGuarantee.scss';

const Logo = () => (
  <svg width="80" height="98" viewBox="0 0 80 98" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M58.6085 12.1908C64.884 15.0334 71.5664 16.9582 78.4373 17.8909V17.8859C79.3303 18.0099 80 18.7442 80 19.6123V54.4529C80 78.3299 42.4309 97.0227 40.8285 97.8065C40.3076 98.0645 39.6875 98.0645 39.1715 97.8065C37.5741 97.0277 0 78.3349 0 54.4529V19.6173C0 18.7491 0.664765 18.0149 1.56269 17.8909C8.43855 16.9582 15.1209 15.0334 21.3915 12.1908C27.7515 9.3035 33.6103 5.50342 38.7647 0.919522C39.4593 0.294445 40.5407 0.294445 41.2353 0.919522C46.3897 5.50838 52.2485 9.3035 58.6085 12.1908ZM40 94.2793C45.4967 91.4119 76.3636 74.3661 76.3636 54.4529V21.1105C69.6813 20.0588 63.1775 18.1191 57.0557 15.336C50.8644 12.5331 45.1246 8.90166 40 4.55092C34.8754 8.90166 29.1356 12.5331 22.9443 15.336C16.8225 18.1191 10.3237 20.0588 3.63636 21.1105V54.4529C3.63636 74.3661 34.5033 91.4119 40 94.2793ZM40 45.2407L45.0552 40.1806L40.129 31.8809C40.0943 31.8313 40.0447 31.8065 40 31.8065C39.9554 31.8065 39.9058 31.8313 39.876 31.8809L34.9448 40.1806L40 45.2407ZM58.0183 46.5206L65.0232 42.8147V42.8098C65.3059 42.661 65.5639 43.0529 65.3605 43.3158C65.3605 43.3158 55.9695 55.6338 53.7271 58.655C53.4592 59.0221 53.0326 59.1908 52.6109 59.1064C48.4388 58.2681 44.222 57.8513 40.0002 57.8513C35.7785 57.8513 31.5567 58.273 27.3895 59.1064C26.9678 59.1908 26.5412 59.0221 26.2683 58.655C24.026 55.6388 14.635 43.3158 14.635 43.3158C14.4365 43.0529 14.6945 42.661 14.9773 42.8098L21.9821 46.5156C23.0239 47.0563 24.021 47.9692 24.2542 49.2292L25.2861 54.4878C25.4796 55.0484 25.9608 55.4006 26.4817 55.4006C26.566 55.4006 26.6503 55.3957 26.7347 55.3758V40.9395L25.3059 39.3371H29.8997L40.0052 49.6162L50.1106 39.3371H54.7044L53.2757 40.9395V55.3758C53.8809 55.5147 54.4911 55.1427 54.7193 54.4928L55.7462 49.2342C55.9843 47.9741 56.9765 47.0613 58.0183 46.5206ZM30.3661 45.2654V54.716C33.2186 54.2844 36.096 54.0463 38.9683 54.0016L30.3661 45.2654ZM41.037 54.0016C43.9094 54.0463 46.7868 54.2844 49.6393 54.716V45.2654L41.037 54.0016ZM52.5263 60.7534C52.305 61.0734 52.0836 61.3958 51.8607 61.7205L51.5539 62.1672C51.286 62.5592 50.8544 62.7526 50.4179 62.6782C46.9651 62.1077 43.4825 61.8249 39.9999 61.8249C36.5173 61.8249 33.0348 62.1127 29.582 62.6782C29.1504 62.7477 28.7138 62.5542 28.4459 62.1672C28.1234 61.691 27.801 61.2197 27.4785 60.7534C31.6209 59.9299 35.8129 59.5181 39.9999 59.5181C44.1869 59.5181 48.3839 59.9299 52.5263 60.7534Z"
      fill="white"
    />
  </svg>
);

const TestosteroneIncreaseGuarantee = ({ step }) => {
  const stepClassName = 'testosterone-increase-guarantee-step';
  useEffect(() => {
    document.documentElement.classList.add(stepClassName);

    return () => document.documentElement.classList.remove(stepClassName);
  }, []);
  useEffect(() => {
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    const previousColor = metaThemeColor?.getAttribute('content') || '#0066a6';
    metaThemeColor?.setAttribute('content', '#000');
    return () => metaThemeColor?.setAttribute('content', previousColor);
  }, []);

  const dispatch = useDispatch();
  const currentProductName = useAppSelector(selectCurrentIntake).get('product_name');

  const onSuccessActionCreator = (_, stateAfterSuccess) =>
    push(selectFurthestProductStepPath({ customer: stateAfterSuccess }, currentProductName));

  const onClick = () => {
    const cmdType = 'answer_questions';
    const params = { [step]: true };
    const action = apiRequestUserCommand({
      cmdType,
      params,
      context: { onSuccessActionCreator },
    });
    dispatch(action);
  };

  return (
    <div className="absolute top-0 left-0 min-h-screen min-w-full flex flex-col items-center px-6 justify-between md:justify-center md:items-center">
      <div className="flex-1 flex flex-col justify-around md:justify-center px-6 py-2 md:pt-0 md:pb-32">
        <div className="my-8 md:my-16 flex flex-col gap-y-6">
          <div className="pb-6">
            <Logo />
          </div>
          <h3>
            Improved testosterone or
            <span className="block text-[#5a7ffc]">you don{"'"}t pay</span>
          </h3>
          <p>See a 10% testosterone boost in the first 3 months, or it{"'"}s free. Simple as that.</p>
        </div>
        <div className="w-full md:mt-8 text-center md:text-left">
          <Button className="max-w-[307px]" onClick={onClick} data-testid="improved-testosterone-submit">
            Got it
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TestosteroneIncreaseGuarantee;
